/**
 * The Instrument menu: what the player at the caret is holding from this bar on.
 *
 * A doubling is written on a bar, so it belongs beside the other things that are: the caret
 * settles both the part and the bar, and every item here is offered greyed out rather than
 * hidden when the caret stands in none.
 *
 * The list offered is the part's own family. A flute player takes the piccolo and the alto
 * flute, a clarinettist the bass clarinet, and nobody hands a violinist a trombone halfway
 * through a piece; a family is the doubling a score actually asks for, and a short list is
 * one a player can read down. The part's own instrument is in it, so a borrowed one can be
 * put down at any later bar rather than only on the bar it was taken up at.
 */

import { INSTRUMENTS, doubling } from "@app/score-editor";
import type { InstrumentPreset, Part, PartId, Score } from "@app/score-editor";
import type { ScoreActions } from "./score-actions";
import { RULE } from "./score-menu-model";
import type { Menu, MenuAction } from "./score-menu-model";

/** An instrument a player can be handed: one with pitches, played on an ordinary staff. */
function playable(preset: InstrumentPreset): boolean {
  return preset.percussion === undefined && preset.tuning === undefined;
}

/**
 * What the part's player could pick up.
 *
 * A part read out of a file may be playing something this editor has no preset for, and
 * then there is no family to go by: the whole catalogue is offered rather than nothing,
 * since a doubling is still a doubling on a part that came from elsewhere.
 */
function choicesFor(part: Part): readonly InstrumentPreset[] {
  const own = INSTRUMENTS.find((preset) => preset.sound === part.instrument.sound) ?? null;
  const pitched = INSTRUMENTS.filter(playable);
  return own === null ? pitched : pitched.filter((preset) => preset.family === own.family);
}

function partAt(score: Score, partId: PartId | null): Part | null {
  return score.parts.find((part) => part.id === partId) ?? null;
}

/** What a player has in their hands at the caret, and what they can be handed instead. */
export function instrumentMenu(actions: ScoreActions): Menu {
  const { score, caret } = actions;
  const part = partAt(score, caret?.partId ?? null);
  const held =
    caret === null || part === null
      ? null
      : doubling.instrumentInHand(score, part.id, caret.measureId);
  const taken =
    caret === null || part === null ? null : (part.measures[caret.measureId]?.instrument ?? null);

  const items: (MenuAction | typeof RULE)[] = [
    {
      label: held === null ? "No part at the caret" : `Playing: ${held.name}`,
      disabled: true,
      run: () => undefined,
    },
    RULE,
  ];

  for (const preset of part === null ? [] : choicesFor(part)) {
    items.push({
      label: `Take up the ${preset.name.toLowerCase()}`,
      disabled: caret === null,
      checked: held?.name === preset.name,
      run: () => {
        if (caret === null || part === null) return;
        actions.edit(
          doubling.takeUpInstrument(score, part.id, caret.measureId, preset),
          "take an instrument up",
        );
      },
    });
  }

  items.push(RULE, {
    label: "Put it down again at this bar",
    disabled: taken === null,
    run: () => {
      if (caret === null || part === null) return;
      actions.edit(
        doubling.putDownInstrument(score, part.id, caret.measureId),
        "change an instrument",
      );
    },
  });

  return { title: "Instrument", items };
}
